import clsx from 'clsx'
import { JurisdictionKind, getJurisdictionPickerValue, type JurisdictionFieldState } from '../jurisdiction-field-state'
import { FEDERAL_DISTRICT_LABELS, STATE_LABELS } from '../jurisdiction-maps'
import { JurisdictionCombobox } from './JurisdictionField'
import { FederalCourthouseIcon, LocationPinIcon } from './JurisdictionIcon'

type DefaultJurisdictionFieldProps = JurisdictionFieldState & {
  onChange: (state: JurisdictionFieldState) => void
  error?: string
  idPrefix?: string
}

const jurisdictionKindOptions: Array<{
  value: JurisdictionKind.STATE | JurisdictionKind.FEDERAL
  label: string
  Icon: typeof LocationPinIcon
}> = [
  { value: JurisdictionKind.STATE, label: 'State', Icon: LocationPinIcon },
  { value: JurisdictionKind.FEDERAL, label: 'Federal', Icon: FederalCourthouseIcon },
]

export default function DefaultJurisdictionField(props: DefaultJurisdictionFieldProps) {
  const { kind, value, onChange, error, idPrefix = 'default-jurisdiction' } = props
  const selectedKind = kind === JurisdictionKind.FEDERAL ? JurisdictionKind.FEDERAL : JurisdictionKind.STATE
  const pickerOptions = selectedKind === JurisdictionKind.FEDERAL ? FEDERAL_DISTRICT_LABELS : STATE_LABELS
  const pickerPlaceholder = selectedKind === JurisdictionKind.FEDERAL ? 'Search U.S. District Courts' : 'Search U.S. States & Territories'

  const handleKindChange = (nextKind: JurisdictionKind) => onChange({ kind: nextKind, value: value })

  return (
    <div className="flex flex-col gap-3">
      <div
        className="inline-flex w-fit items-center gap-0.5 rounded-full border border-brand-neutral-300 p-[3px]"
        role="radiogroup"
        aria-label="Default jurisdiction type"
      >
        {jurisdictionKindOptions.map(({ value: optionValue, label, Icon }) => {
          const isSelected = selectedKind === optionValue
          return (
            <label key={optionValue} className="cursor-pointer">
              <input
                type="radio"
                name={`${idPrefix}-kind`}
                value={optionValue}
                checked={isSelected}
                className="peer sr-only"
                onChange={() => handleKindChange(optionValue)}
              />
              <span
                className={clsx(
                  'flex items-center gap-1 rounded-full px-2 py-px text-sm font-semibold leading-5 tracking-[-0.15px] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-500',
                  isSelected ? 'bg-brand-neutral-300 text-brand-neutral-950' : 'text-true-gray-600 hocus:bg-brand-neutral-100'
                )}
              >
                <Icon className="size-4" />
                {label}
              </span>
            </label>
          )
        })}
      </div>

      <JurisdictionCombobox
        id={`${idPrefix}-picker`}
        options={pickerOptions}
        value={getJurisdictionPickerValue(selectedKind, value)}
        onChange={(nextValue) => onChange({ kind: selectedKind, value: nextValue })}
        placeholder={pickerPlaceholder}
        error={error}
        matchMode={selectedKind === JurisdictionKind.STATE ? 'prefix' : 'contains'}
      />
    </div>
  )
}
